import { Moment } from 'moment';

import { Nullable, Nullish } from './common';
import { UploadFileLike } from './components';

import {
  Balance,
  Country,
  CountryCode,
  CurrencyCode,
  FileType,
  Invoice,
  InvoiceStatus,
  PaymentMethod,
  PaymentStatus,
  Payout,
  ProfileStatus,
  WithdrawalMethod,
  WithdrawalStatus,
} from './models';

export enum AuthEvent {
  CREDENTIALS = 'credentials',
}

export enum AuthEndpoint {
  LOGIN = '/auth/sign-in',
  LOGOUT = '/auth/sign-out',
  PASSWORD_RESET = '/auth/forgot-password',
  PASSWORD_RESET_CONFIRM = '/auth/forgot-password-confirm',
  REFRESH_ACCESS_TOKEN = '/auth/refresh',
  REGISTER = '/auth/sign-up',
  REGISTER_VERIFY = '/auth/send-code',
  REGISTER_CONFIRM = '/auth/verify-code',
}

export type DateRangeable = {
  dateFrom?: Moment;
  dateTo?: Moment;
};

export type Paginatable = {
  page?: number;
  size?: number;
};

export type PaginatedResponse<Type> = {
  content: Type[];
  totalElements: number;
  pageable: {
    pageNumber: number;
    pageSize: number;
  };
};

export type PaginatedData<Type> = {
  list: Type[];
  page: number;
  size: number;
  total: number;
};

export type Searchable = {
  search?: string;
};

export type Timezonable = {
  timezone?: string;
};

export type AuthCredentials = {
  accessToken: string;
  refreshToken: string;
  status: ProfileStatus;
  remember: boolean;
};

export type AuthLoginParams = {
  email: string;
  password: string;
  remember?: boolean;
};

export type AuthLoginResponse = {
  accessToken: string;
  refreshToken: string;
  status: ProfileStatus;
};

export type AuthPasswordResetParams = {
  email: string;
};

export type AuthPasswordResetConfirmParams = AuthPasswordResetParams & {
  password: string;
  code: string;
};

export type AuthRefreshAccessTokenParams = {
  refreshToken: string;
};

export type AuthRegistrationParams = {
  email: string;
  password: string;
};

export type AuthRegistrationVerifyParams = {
  email: string;
};

export type AuthRegistrationConfirmParams = AuthRegistrationParams & {
  code: string;
};

export type CountriesResponse = {
  list: Country[];
};

export type FileParams = {
  fileType: FileType;
};

export type FileUploadParams = {
  fileType: FileType;
  file: UploadFileLike;
};

export type InvoicesParams = DateRangeable & Paginatable & Searchable & Timezonable & {
  amountFrom?: number;
  amountTo?: number;
  status?: InvoiceStatus;
};

export type InvoicesResponse = PaginatedResponse<Invoice>;

export type InvoiceCreateParams = {
  purpose: string;
  amount: number;
};

export type InvoiceCreateResponse = {
  displayId: string;
};

export type PaymentCreateBaseParams = {
  method: PaymentMethod;
  invoiceId: string;
  email: string;
  purpose?: Nullish<string>;
};

export type PaymentCardCreateParams = PaymentCreateBaseParams & {
  method: PaymentMethod.CARD;
  card_number: string;
  cvv: string;
  expiry_month: string;
  expiry_year: string;
};

export type PaymentCardAvsVerificationParams = {
  country: CountryCode;
  state: string;
  city: string;
  address: string;
  zipcode?: Nullish<string>;
};

export type PaymentCardOtpVerificationParams = {
  otpCode: string;
};

export type PaymentCardPinVerificationParams = {
  pin: string;
};

export type PaymentFrancophoneCreateParams = PaymentCreateBaseParams & {
  method: PaymentMethod.FRANCOPHONE;
  phone: string;
};

export type PaymentResponse = {
  trxRef: string;
  message: string;
  redirectUrl: string;
};

export type PaymentStatusResponse = {
  trxRef: string;
  email: string;
  companyName: string;
  amount: number;
  currency: CurrencyCode;
  purpose: string;
  errorMessage?: Nullish<string>;
  redirectUrl?: Nullish<string>;
  status: PaymentStatus;
  createdDate: string;
  updatedDate: string;
  invoice: {
    id: string;
    displayId: string;
    amount: number;
    currency: CurrencyCode;
    status: InvoiceStatus;
    purpose: string;
  };
};

export type PayoutsParams = DateRangeable & Paginatable & Timezonable & {
  status?: WithdrawalStatus;
};

export type PayoutsResponse = PaginatedResponse<Payout>;

export type ProfileUpdateParams = {
  name: string;
  companyName: string;
  countryCode: string;
  phone: string;
};

export type ProfileBalancesResponse = {
  balances: Balance[];
};

export type ProfilePasswordUpdateParams = {
  oldPassword: string;
  newPassword: string;
};

export type StatisticsFeesParams = DateRangeable & Timezonable;

export type StatisticsFeesResponse = {
  list: Array<{
    amount: number;
    day: string;
  }>;
};

export type StatisticsPaymentsParams = DateRangeable & Timezonable;

export type StatisticsPaymentsResponse = {
  list: Array<{
    amount: number;
    day: string;
  }>;
};

export type StatisticsPayoutsParams = DateRangeable & Timezonable;

export type StatisticsPayoutsResponse = {
  list: Array<{
    amount: number;
    day: string;
  }>;
};

export type StatisticsVolumesParams = DateRangeable & Timezonable;

export type StatisticsVolumesResponse = {
  list: Array<{
    input: number;
    output: number;
    balance: number;
    day: string;
  }>;
};

export type WithdrawalParams = {
  withdrawalMethod: WithdrawalMethod;
  details: Nullable<{
    bankCode?: Nullish<string>;
    accountNumber?: Nullish<string>;
  }>;
};
